<template>
  <div
    ref="layoutVideo"
    class="videoStream videoPlayback"
    :class="{
      fullscreen: fullscreen,
    }"
    @mouseover="handleDisplayMouseHover"
    @mouseleave="handleDisplayMouseLeave"
  >
    <skeleton-video v-if="loading" style="position: absolute" />
    <no-image v-if="!loading && isError" :absolute="true" />
    <div
      class="videoStream_container"
      :class="{
        ['videoStream_hide']: isError,
        ['videoStream_visible']: !isError,
      }"
    >
      <div class="videoStream_type videoStream_type-vod">VOD</div>
      <div
        class="videoStream_video videoStream_video-main"
        :class="{
          'videoStream_video-fullscreen': fullscreen,
        }"
      >
        <div class="parent-zoom">
          <div ref="videoWrapper" @dblclick="handleDblclick">
            <video
              v-if="currentFragment"
              ref="playerElement"
              :muted="muted"
              class="video"
              :poster="poster"
              disablePictureInPicture
              @click="handleDisplayMouseClick"
              @timeupdate="handleUpdateVideoTime"
              @play="startTracking"
              @pause="stopTracking"
              @error="handleError"
              @loadedmetadata="handleLoadMetadata"
              @ended="handleEndedVideo"
              @canplaythrough="handleCanPlayThrough"
            >
              <source
                ref="playerSrc"
                :src="currentFragment.url"
                type="video/mp4"
                @error="handleError"
              />
            </video>
            <video
              v-else
              class="video videoStream_video-blank"
              disablePictureInPicture
            />
          </div>
        </div>
        <v-btn
          v-if="clipMenu"
          text
          class="
            videoStream_display videoStream_display-icon videoStream_more-action
          "
          :class="{
            videoStream_visible: showDisplayController && !isError,
          }"
          @click="handlePrevent"
        >
          <span class="icon icon-3dots" />
        </v-btn>
        <v-btn
          text
          class="
            videoStream_display
            videoStream_display-icon
            videoStream_fullscreen-action
          "
          :class="{
            videoStream_visible: showDisplayController && !isError,
            'videoStream_fullscreen-action-fullscreen': fullscreen,
          }"
          @click="toggleFullscreen"
        >
          <span class="icon icon-fullscreen" />
        </v-btn>
        <slider
          v-model="zoom"
          :min="10"
          :max="maxZoom"
          class="videoStream_display videoStream_display-slider"
          :class="{
            videoStream_visible: showDisplayController && !isError,
            'videoStream_slider-fullscreen': fullscreen,
          }"
          @change="handleZoom"
          @click="handlePrevent"
        />
        <slot
          :_class="{
            videoStream_videoseek: true,
            'videoStream_videoseek-show': fullscreen && showDisplayController,
          }"
          :show="fullscreen && showDisplayController"
        />
      </div>
    </div>
    <slot name="footer" />
  </div>
</template>

<script>
import Slider from './inputs/ZoomSlider.vue';
import panzoom from 'panzoom';
import {
  getScaleMultiplier,
  getTransformOriginOffset,
  getOffsetXY,
} from '../utils/coordinates';
import dayjs from 'dayjs';
import NoImage from './NoImage.vue';
const approx30MinInSec = 1799;
export default {
  name: 'VideoPlayback',
  components: {
    Slider,
    NoImage,
  },
  props: {
    fragmentMeta: {
      type: Object,
      default: () => ({}),
    },
    loading: {
      type: Boolean,
      default: true,
    },
    live: {
      type: Boolean,
      default: true,
    },
    disableZoom: {
      type: Boolean,
      default: false,
    },
    playing: {
      type: Boolean,
      default: true,
    },
    controllerSize: {
      type: String,
      default: 'large',
    },
    setTime: {
      type: Object,
      default: () => {},
    },
    playbackRate: {
      type: Number,
      default: 1,
    },
    hideDisplayController: {
      type: Boolean,
      default: false,
    },
    zoomoutOnRestore: {
      type: Boolean,
      default: false,
    },
    event: {
      type: Array,
      default: () => [],
    },
    showError: {
      type: Boolean,
      default: false,
    },
    muted: {
      type: Boolean,
      default: true,
    },
    clipMenu: {
      type: Boolean,
      default: false,
    },
    startTimestamp: {
      type: Number,
      default: 0,
    },
    endTimestamp: {
      type: Number,
      default: 0,
    },
  },
  data: () => ({
    currentTimeIsChanging: false,
    url: null,
    isInitialized: false,
    fragmentIndex: 0,
    isError: false,
    displayControllerVisible: false,
    fullscreen: false,
    timestamp: undefined,
    zoom: 10,
    maxZoom: 100,
    panzoomInstance: null,
    poster: '/img/skeleton.png',
    videoCanvas: document.createElement('canvas'),
    videoContext: null,
    videoMetadata: {
      w: 0,
      h: 0,
      ratio: 0,
    },
    reqId: null,
  }),
  computed: {
    showDisplayController() {
      return !this.hideDisplayController && this.displayControllerVisible;
    },
    isSafari() {
      return this.$device.isSafari;
    },
    isPlaying: {
      get() {
        return this.playing;
      },
      set(val) {
        this.$emit('update:playing', val);
      },
    },
    signalVal: {
      get() {
        return this.signal;
      },
      set(val) {
        this.$emit('update:signal', val);
      },
    },
    isLive: {
      get() {
        return this.live;
      },
      set(val) {
        this.$emit('update:live', val);
      },
    },
    playbackRateValue: {
      get() {
        return this.playbackRate;
      },
      set(val) {
        this.$emit('update:playbackRate', val);
      },
    },
    fragments() {
      return this.fragmentMeta ? this.fragmentMeta.fragments : [];
    },
    currentFragment() {
      return this.fragments[this.fragmentIndex];
    },
    lastFragmentIndex() {
      return this.fragments.length - 1;
    },
  },
  watch: {
    playbackRate(val) {
      this.$refs.playerElement.playbackRate = val;
    },
    showError() {
      this.isError = this.showError;
    },
    isPlaying(val) {
      const player = this.$refs.playerElement;
      if (!player) {
        return;
      }
      if (val) {
        this.play();
      } else {
        this.pause();
      }
    },
    setTime: {
      handler: function (val) {
        const valUnix = val.valueOf();

        if (valUnix > this.startTimestamp && valUnix < this.endTimestamp) {
          const diffMs = dayjs(valUnix).diff(dayjs(this.startTimestamp));
          this.goToTime(diffMs / 1000);
        }
      },
    },
    fragments: {
      handler(val) {
        if (val.length !== 0) {
          this.init();
        }
      },
    },
  },
  beforeDestroy() {
    if (this.panzoomInstance) {
      this.panzoomInstance.dispose();
    }
    this.stopTracking();
  },
  mounted() {
    window.addEventListener('keyup', this.handleEsc);
  },
  methods: {
    init() {
      this.url = null;
      this.fragmentIndex = 0;
      this.isError = false;
    },
    hasAudio(videoElement) {
      // ensure the video metadata has been loaded
      if (videoElement.readyState >= 1) {
        // '1' means loaded metadata
        const hasAudioTracks =
          videoElement.audioTracks && videoElement.audioTracks.length > 0;
        const isMutedOrSilent = videoElement.muted || videoElement.volume === 0;

        if (typeof videoElement.audioTracks !== 'undefined') {
          return hasAudioTracks && !isMutedOrSilent;
        }

        // check for decoded audio in Safari and other non-audioTracks-supporting browsers
        const hasAudioStream =
          videoElement.webkitAudioDecodedByteCount > 0 ||
          videoElement.mozHasAudio;

        return hasAudioStream || !isMutedOrSilent;
      } else {
        console.log('Metadata not loaded yet.');
        return false;
      }
    },
    handleCanPlayThrough(e) {
      this.$emit('canplaythrough', e);
    },
    playTracking() {
      const video = this.$refs.playerElement;
      if (!video) {
        return;
      }
      const currentMs = Math.round(video.currentTime * 1000);
      const videoTime = dayjs(this.startTimestamp)
        .add(currentMs, 'ms')
        .add(approx30MinInSec * this.fragmentIndex, 'seconds')
        .valueOf();
      this.updateControlbarTime(videoTime);
      this.reqId = requestAnimationFrame(this.playTracking.bind(this));
    },
    startTracking() {
      cancelAnimationFrame(this.reqId);
      const video = this.$refs.playerElement;
      const currentMs = Math.round(video.currentTime * 1000);
      const videoTime = dayjs(this.startTimestamp)
        .add(currentMs, 'ms')
        .add(approx30MinInSec * this.fragmentIndex, 'seconds')
        .valueOf();
      this.updateControlbarTime(videoTime);
      this.reqId = requestAnimationFrame(this.playTracking.bind(this));
    },
    stopTracking() {
      if (this.isSafari) {
        // To prevent Safari from displaying a blank video frame, we force the browser to redraw the video element.
        // this is accomplished by triggering a reflow, which essentially tells Safari to recalculate and re-render the video's visual representation.
        this.reflowVideo();
      }
      cancelAnimationFrame(this.reqId);
    },
    reflowVideo() {
      const video = this.$refs.playerElement;
      const currentTime = video.currentTime;
      video.style.display = 'none';
      video.offsetHeight; // trigger a reflow
      video.style.display = '';
      video.currentTime = currentTime;
    },
    handleUpdateVideoTime(e) {
      if (this.currentTimeIsChanging) {
        return;
      }
      if (!this.isPlaying) {
        const { currentTime } = e.target;
        const currentMs = Math.round(currentTime * 1000);
        const videoTime = dayjs(this.startTimestamp)
          .add(currentMs, 'millisecond')
          .valueOf();
        this.updateControlbarTime(videoTime);
      }
    },
    updateControlbarTime(timestamp) {
      this.$emit('timestamp', timestamp);
      this.timestamp = timestamp;
    },
    handleError(error) {
      this.$emit('onError', { error, fragmentIndex: this.fragmentIndex });
    },
    loadPlayer() {
      const player = this.$refs.playerElement;
      player.load();
      player.playbackRate = this.playbackRate;
      if (!this.checkVideoPlaying() && this.isPlaying) {
        this.play();
      }
    },
    checkVideoPlaying() {
      const player = this.$refs.playerElement;
      return !!(
        player.currentTime > 0 &&
        !player.paused &&
        !player.ended &&
        player.readyState > 2
      );
    },
    handleEndedVideo() {
      if (this.fragmentIndex === this.lastFragmentIndex) {
        this.isPlaying = false;
        return;
      }
      const nextFragmentIndex = this.fragmentIndex + 1;
      const nextFragment = this.fragments[nextFragmentIndex];
      if (nextFragment) {
        // if new fragment exist, play to new fragment
        this.fragmentIndex = nextFragmentIndex;
        this.$nextTick(() => {
          const player = this.$refs.playerElement;
          this.loadPlayer();
          player.currentTime = 0;
        });
      }
    },
    play() {
      const player = this.$refs.playerElement;
      const promise = player.play();
      if (promise !== undefined) {
        promise.catch((e) => {
          if (e.name === 'NotAllowedError' || e.name === 'NotSupportedError') {
            // check not allowed
          }
        });
      }
    },
    pause() {
      const player = this.$refs.playerElement;
      player.pause();
    },
    handleLoadMetadata(evt) {
      const video = evt.target;
      // Calculate the ratio of the video's width to height
      this.videoMetadata.ratio = video.videoWidth / video.videoHeight;
      // Define the required width as 100 pixels smaller than the actual video's width
      this.videoMetadata.w = video.videoWidth - 100;
      // Calculate the height based on the video's width and the ratio
      this.videoMetadata.h = parseInt(
        this.videoMetadata.w / this.videoMetadata.ratio,
        10
      );
      // Set the canvas width and height to the values just calculated
      this.videoCanvas.width = this.videoMetadata.w;
      this.videoCanvas.height = this.videoMetadata.h;
      // Set the canvas context to the video element
      this.videoContext = this.videoCanvas.getContext('2d');
      if (!this.isInitialized) {
        this.isInitialized = true;
        this.isPlaying = true;
      }
      this.$emit('onVideoLength');
      window['initalPanZoomTimeout' + this.id] = setTimeout(() => {
        this.initialPanZoom();
        // const hasAudio = this.hasAudio(video);
        // this.$emit('onMeta', { hasAudio });
      }, 1000);
    },
    snap() {
      const { w, h } = this.videoMetadata;
      const video = this.$refs.playerElement;
      // Define the size of the rectangle that will be filled (basically the entire element)
      this.videoContext.fillRect(0, 0, w, h);
      // Grab the image from the video
      this.videoContext.drawImage(video, 0, 0, w, h);
      return this.videoCanvas;
    },
    snapOption({ w, h }) {
      const video = this.$refs.playerElement;

      const canvas = document.createElement('canvas');
      // Set the canvas width and height to the values just calculated
      canvas.width = w;
      canvas.height = h;

      // Set the canvas context to the video element
      const ctx = canvas.getContext('2d');
      // Define the size of the rectangle that will be filled (basically the entire element)
      ctx.fillRect(0, 0, w, h);
      // Grab the image from the video
      ctx.drawImage(video, 0, 0, w, h);
      return canvas;
    },
    handleEsc(e) {
      if (e.keyCode === 27 && this.fullscreen) {
        this.toggleFullscreen();
      }
    },
    handleDblclick(e) {
      this.toggleFullscreen(e);
      e.preventDefault();
      e.stopPropagation();
    },
    initialPanZoom() {
      var element = this.$refs.videoWrapper;
      if (element) {
        const owner = element.parentElement;
        const transformOrigin = null;
        this.panzoomInstance = panzoom(element, {
          maxZoom: this.maxZoom / 10,
          minZoom: 1,
          bounds: true,
          boundsPadding: 1,
          smoothScroll: false,
          beforeWheel: (e) => {
            if (this.disableZoom) {
              return true;
            }
            var delta = e.deltaY;
            if (e.deltaMode > 0) delta *= 100;

            var scaleMultiplier = getScaleMultiplier(delta, 0.3);

            if (scaleMultiplier !== 1) {
              var offset = transformOrigin
                ? getTransformOriginOffset(owner)
                : getOffsetXY(e, owner);
              this.zoom =
                this.panzoomInstance.getTransform().scale.toFixed(1) * 10;
              this.panzoomInstance.zoomTo(offset.x, offset.y, scaleMultiplier);
              e.preventDefault();
            }
            return true;
          },
        });
      }
    },
    // handle change zoom
    handleZoom(zoomValue) {
      if (!this.panzoomInstance) {
        this.initialPanZoom();
      }
      const centerX =
        this.$refs.videoWrapper.offsetLeft +
        this.$refs.videoWrapper.offsetWidth / 2;
      const centerY =
        this.$refs.videoWrapper.offsetTop +
        this.$refs.videoWrapper.offsetHeight / 2;
      this.panzoomInstance.zoomAbs(centerX, centerY, zoomValue / 10);
    },
    handlePrevent(e) {
      e.preventDefault();
    },
    handleDisplayMouseClick() {
      if (this.fullscreen) {
        this.displayControllerVisible = !this.displayControllerVisible;
      }
    },
    handleDisplayMouseHover() {
      if (!this.fullscreen && !this.isError) {
        this.displayControllerVisible = true;
      }
    },
    handleDisplayMouseLeave() {
      if (!this.fullscreen && !this.isError) {
        this.displayControllerVisible = false;
      }
    },
    // Toggling fullscreen mode
    toggleFullscreen(e) {
      if (e) {
        e.stopPropagation();
      }
      if (this.fullscreen && this.zoomoutOnRestore) {
        this.handleZoom(10);
      }
      this.fullscreen = !this.fullscreen;
      this.displayControllerVisible = false;
      this.$emit('onFullscreenChange', { fullscreen: this.fullscreen });
    },
    goToTime(val) {
      this.currentTimeIsChanging = true;
      const fragmentIndexByTime = parseInt(val / approx30MinInSec);
      const fragmentIndexChange = this.fragmentIndex !== fragmentIndexByTime;
      this.fragmentIndex = fragmentIndexByTime;

      const currentTime = val - this.fragmentIndex * approx30MinInSec;

      this.$nextTick(() => {
        const player = this.$refs.playerElement;
        if (fragmentIndexChange) {
          this.loadPlayer();
          player.currentTime = currentTime;
        } else {
          player.currentTime = currentTime;
        }
        this.currentTimeIsChanging = false;
      });
    },
  },
};
</script>

<style lang="scss">
.videoStream {
  position: relative;
  .videoSkeleton {
    height: calc(100% - 30px);
  }
  .parent-zoom {
    position: relative;
    overflow: hidden;
    user-select: none;
    touch-action: none;
    &.w-h-100 {
      width: 100%;
      height: 100%;
    }
  }
  &_hide {
    pointer-events: none;
    visibility: hidden;
    & > * {
      pointer-events: none;
      visibility: hidden;
    }
  }
  position: relative;
  display: flex;
  flex-direction: column;
  &_container {
    visibility: hidden;
  }
  &_dragging {
    padding: 4px;
    border-radius: 4px;
    box-shadow: 0 3px 12px 0 rgba(0, 0, 0, 0.12);
    background-color: #fff;
  }
  &_videoseek {
    position: absolute;
    bottom: 20px;
    width: 100%;
    left: 0;
    z-index: 21;
    padding: 0 20px;
    visibility: hidden;
    .vis-timeline {
      visibility: hidden !important;
    }
    &-show {
      visibility: visible;
      .vis-timeline {
        visibility: visible !important;
      }
    }
  }
  &.fullscreen {
    background-color: #4b4b4b;
    width: 100%;
    height: 100%;
    position: fixed;
    top: 0;
    left: 0;
    z-index: 20;
  }
  &_slider {
    &-fullscreen {
      padding: 30px 10px 70px;
    }
  }
  &_visible {
    visibility: visible !important;
  }
  &_more-action {
    position: absolute;
    top: 10px;
    right: 10px;
  }
  &_drag-action {
    position: absolute;
    margin: 0 auto;
    top: -2px;
    left: 0;
    right: 0;
  }
  &_fullscreen-action {
    position: absolute;
    bottom: 20px;
    right: 10px;
    &-fullscreen {
      bottom: 70px;
    }
  }
  &_display {
    visibility: hidden;
    z-index: 22;
    &-icon {
      &.v-btn:not(.v-btn--round).v-size--default {
        height: 31px;
        padding: 0 5px;
        min-width: 31px;
      }
      .icon {
        font-size: 20px;
        color: #fff;
      }
    }
    &-slider {
      position: absolute;
      bottom: 0;
      left: 0;
    }
  }
  &_no-data {
    background-color: #8c8c8a85;
    display: flex;
    height: calc(100% - 35px);
    justify-content: center;
    align-items: center;
    position: absolute;
    width: 100%;
  }
  &_type {
    position: absolute;
    top: 10px;
    left: 10px;
    z-index: 1;
    line-height: 16px;
    padding: 0 5px;
    border-radius: 3px;
    font-family: OpenSans;
    font-size: 12px;
    color: #fff;
    pointer-events: none;
    &-live {
      background-color: var(--secondary-color-lighten1);
    }
    &-vod {
      background-color: var(--primary-color-lighten1);
    }
  }
  &_video {
    width: 100%;
    position: relative;
    overflow: hidden;
    &-fullscreen {
      position: unset;
      overflow: unset;
      video {
        width: 100vw;
        height: 100vh;
      }
    }
    &-blank {
      min-height: 300px;
    }
    video {
      width: 100%;
    }
  }
  &_signal {
    margin-right: 5px;
    &.icon-wifi {
      color: var(--secondary-color);
    }
    &.icon-wifi-off {
      color: #fc6d00;
    }
    &.icon-sync {
      color: var(--primary-color);
    }
  }
  &_title {
    font-size: 14px;
    font-weight: 600;
    color: var(--text-color);
  }
  &.fullscreen {
    button {
      &.v-btn:not(.v-btn--round).v-size--default {
        width: 45px;
        height: 45px;
        margin: 0px 2px;
      }
    }
    .videoSeek_vod_liveToggle {
      height: auto !important;
      &--content {
        padding: 14px 15px;
        display: flex;
        cursor: pointer;
        position: relative;
        .v-btn__content {
          line-height: 1;
        }
        &:hover {
          &::before {
            opacity: 0.08;
            background-color: currentColor;
            border-radius: inherit;
            bottom: 0;
            color: inherit;
            content: '';
            left: 0;
            pointer-events: none;
            position: absolute;
            right: 0;
            top: 0;
            transition: opacity 0.2s cubic-bezier(0.4, 0, 0.6, 1);
            border-radius: 5px;
          }
        }
      }
    }
    .vue-slider {
      padding: 0px 20px !important;
      cursor: pointer;
    }
    .videoSeek_vod_controls {
      width: auto;
    }
    .slider_icon {
      padding: 12px !important;
    }
    .controlbar_vod_speed,
    .videoSeek_vod_speed {
      .v-input__slot {
        padding: 4px;
      }
    }
  }
}
</style>
